import { Empty, Typography } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/de";
import { observer } from "mobx-react-lite";
import React from "react";
import type { VolunteerTask, VolunteerSlot } from "../types";
import type { VolunteerStore } from "../store";
import { SlotCard } from "./SlotCard";
import { wrapText } from "./responsive";

interface Props {
  store: VolunteerStore;
  isAdmin: boolean;
}

interface Pair {
  task: VolunteerTask;
  slot: VolunteerSlot;
}

function comparePairs(a: Pair, b: Pair): number {
  const aStart = a.slot.startTime?.getTime() ?? 0;
  const bStart = b.slot.startTime?.getTime() ?? 0;
  return (
    aStart - bStart ||
    a.task.order - b.task.order ||
    a.task.title.localeCompare(b.task.title)
  );
}

export const TimeScheduleView = observer(({ store, isAdmin }: Props) => {
  dayjs.locale("de");

  const pairs: Pair[] = store.activeTasks.flatMap((task) =>
    store.slotsForTask(task.objectId!).map((slot) => ({ task, slot })),
  );

  const startTimes = store.activeTasks.flatMap((task) =>
    store.slotsForTask(task.objectId!).map((slot) => slot.startTime),
  );

  const earliestStart = startTimes.reduce(
    (a, b) => (a?.getTime() ?? Infinity) < (b?.getTime() ?? Infinity) ? a : b,
  );

  const timedPairs = pairs.filter((p) => p.slot.startTime && p.slot.endTime);
  const untimedPairs = pairs
    .filter((p) => !(p.slot.startTime && p.slot.endTime))
    .sort(comparePairs);

  const dayGroups = new Map<string, Pair[]>();
  const dayGroupsTimes = [
    earliestStart,
    new Date("2026-09-18T13:00:00"),
    new Date("2026-09-18T17:00:00"),
    new Date("2026-09-19T08:00:00"),
    new Date("2026-09-19T12:00:00"),
    new Date("2026-09-19T16:00:00"),
    new Date("2026-09-20T08:00:00"),
    new Date("2026-09-20T12:00:00"),
  ]
  for (const time of dayGroupsTimes) {
    const key = dayjs(time).format("YYYY-MM-DD HH:mm");
    if (!dayGroups.has(key)) dayGroups.set(key, []);
  }
  for (const pair of timedPairs) {
    for (let i = dayGroupsTimes.length - 1; i >= 0; i--) {
      if (pair.slot.startTime && pair.slot.startTime >= dayGroupsTimes[i]) {
        const key = dayjs(dayGroupsTimes[i]).format("YYYY-MM-DD HH:mm");
        dayGroups.get(key)!.push(pair);
        break;
      }
    }
    // const key = dayjs(pair.slot.startTime).format("YYYY-MM-DD");
    // if (!dayGroups.has(key)) dayGroups.set(key, []);
    // dayGroups.get(key)!.push(pair);
  }
  const sortedDayKeys = Array.from(dayGroups.keys()).sort();
  for (const key of sortedDayKeys) {
    dayGroups.get(key)!.sort(comparePairs);
  }

  if (pairs.length === 0) {
    return <Empty description="Noch keine Schichten veröffentlicht" />;
  }

  return (
    <div>
      {sortedDayKeys.map((key) => (
        <div key={key} style={{ marginBottom: 24 }}>
          <Typography.Title level={4} style={wrapText}>
            {dayjs(key).format("ddd, DD.MM.YYYY HH:mm")} Uhr - {dayjs(sortedDayKeys[sortedDayKeys.indexOf(key)+1] ?? new Date("2026-09-20T23:59:00")).format("HH:mm")} Uhr
          </Typography.Title>
          {dayGroups.get(key)!.map((pair) => (
            <SlotCard
              key={pair.slot.objectId}
              store={store}
              task={pair.task}
              slot={pair.slot}
              isAdmin={isAdmin}
              taskTag={pair.task.title}
            />
          ))}
        </div>
      ))}

      {untimedPairs.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <Typography.Title level={4} style={wrapText}>Ohne feste Zeit</Typography.Title>
          {untimedPairs.map((pair) => (
            <SlotCard
              key={pair.slot.objectId}
              store={store}
              task={pair.task}
              slot={pair.slot}
              isAdmin={isAdmin}
              taskTag={pair.task.title}
            />
          ))}
        </div>
      )}
    </div>
  );
});
